// ProductModal — the one create/edit form for a product, shared by the
// top-level Products PIM and the per-company catalog page.
//
// It lived inside pages/Products.tsx, which is why the top-level Products page
// could only ever link to that page instead of editing anything itself. Nothing
// about the form was per-page; it just wasn't reachable.
//
// Money is bigint minor units end-to-end: unit_rate_minor is parsed from a
// major-unit text input to bigint on save and never round-trips through a
// float.
//
// CATALOG HANDLING — the one non-obvious part. go-pim's product update is a
// FULL-ROW replace that deliberately IGNORES catalog_id, so that a client which
// omits the field cannot silently blank a product's grouping. Moving is its own
// endpoint. So on save this does:
//
//   create  POST with catalog_id in the body (honoured there)
//   edit    PUT the product, then — only if the operator changed it — a
//           separate moveProductToCatalog call
//
// The operator still sees one form with one Save button; the two-call sequence
// is this component's problem, not theirs.
import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal } from "@/components/UI";
import { parseMinorFromMajor, minorToMajorInput } from "@/utils/money";
import { apiErrorMessage } from "@/utils/apiError";
import { useUI } from "@/store/ui";
import { moveProductToCatalog, type Catalog } from "@/api/catalogs";
import {
  createProduct,
  updateProduct,
  type Product,
  type ProductWritePayload,
  type BusinessType,
  type TaxScheme,
} from "@/api/products";

const BUSINESS_TYPES: BusinessType[] = [
  "donation",
  "livestock",
  "solar",
  "generic",
];
const TAX_SCHEMES: TaxScheme[] = ["none", "gst", "solar_70_30"];

type AttrPair = { key: string; value: string };

interface ProductFormState {
  name: string;
  description: string;
  category: string;
  business_type: BusinessType;
  hsn_sac: string;
  unit: string;
  unit_rate_major: string; // major-unit text; parsed to minor bigint on save
  currency: string;
  tax_scheme: TaxScheme;
  gst_rate_bps: string; // text; parsed to int on save
  attributes: AttrPair[];
  active: boolean;
  catalog_id: string; // "" = ungrouped
}

function emptyForm(catalogId: string): ProductFormState {
  return {
    name: "",
    description: "",
    category: "",
    business_type: "generic",
    hsn_sac: "",
    unit: "",
    unit_rate_major: "",
    currency: "INR",
    tax_scheme: "none",
    gst_rate_bps: "0",
    attributes: [],
    active: true,
    catalog_id: catalogId,
  };
}

function formFromProduct(p: Product): ProductFormState {
  return {
    name: p.name,
    description: p.description,
    category: p.category,
    business_type: p.business_type,
    hsn_sac: p.hsn_sac,
    unit: p.unit,
    unit_rate_major: minorToMajorInput(p.unit_rate_minor),
    currency: p.currency,
    tax_scheme: p.tax_scheme,
    gst_rate_bps: String(p.gst_rate_bps),
    attributes: Object.entries(p.attributes).map(([key, value]) => ({
      key,
      value,
    })),
    active: p.active,
    catalog_id: p.catalog_id,
  };
}

export function ProductModal({
  companyId,
  product,
  catalogs,
  defaultCatalogId = "",
  open,
  onClose,
}: {
  companyId: string;
  product: Product | null; // null = create
  catalogs: Catalog[];
  /** Pre-select this catalog when creating (the one being viewed). */
  defaultCatalogId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const addToast = useUI((s) => s.addToast);
  const [form, setForm] = useState<ProductFormState>(() =>
    product ? formFromProduct(product) : emptyForm(defaultCatalogId),
  );
  const [error, setError] = useState<string | null>(null);
  // Which fields the operator has actually visited. Errors are shown only for
  // touched fields, so a freshly-opened blank form is not already red.
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Live field validation. Previously the ONLY feedback was a single line at
  // the bottom of the form, and only after pressing Save — so a mistyped rate
  // meant filling in every other field, submitting, then hunting for what was
  // wrong. These run as you type and mark the offending input.
  const nameError =
    touched.name && form.name.trim() === "" ? "Name is required." : null;
  const rateError =
    touched.rate && parseMinorFromMajor(form.unit_rate_major) == null
      ? "Enter an amount like 1499 or 1499.50."
      : null;
  const currencyError =
    touched.currency && form.currency.trim().length !== 3
      ? "A 3-letter code, e.g. INR."
      : null;
  const gstError =
    touched.gst &&
    form.tax_scheme === "gst" &&
    (() => {
      const n = parseInt(form.gst_rate_bps, 10);
      return Number.isNaN(n) || n < 0 || n > 10000;
    })()
      ? "0–10000 basis points (1800 = 18%)."
      : null;
  const hasFieldError = Boolean(nameError || rateError || currencyError || gstError);

  const markTouched = (k: string) => setTouched((t) => ({ ...t, [k]: true }));

  // Re-seed when the modal target changes (opened on a different product, or
  // switched between create and edit). defaultCatalogId is part of the key so
  // opening "New product" from inside a different catalog pre-selects it.
  const seedKey = `${product?.id ?? "new"}:${defaultCatalogId}`;
  const [lastSeed, setLastSeed] = useState(seedKey);
  if (open && lastSeed !== seedKey) {
    setForm(product ? formFromProduct(product) : emptyForm(defaultCatalogId));
    setError(null);
    setTouched({});
    setLastSeed(seedKey);
  }

  const mutation = useMutation({
    mutationFn: async (payload: ProductWritePayload) => {
      if (!product) {
        return createProduct(companyId, payload);
      }
      const saved = await updateProduct(product.id, payload);
      // updateProduct ignores catalog_id by design — move separately, and only
      // when it actually changed, so an ordinary edit costs one request.
      if (form.catalog_id !== product.catalog_id) {
        await moveProductToCatalog(product.id, form.catalog_id);
      }
      return saved;
    },
    onSuccess: async (saved) => {
      addToast({
        title: product ? "Product saved" : "Product created",
        body: saved?.name,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["products"] }),
        qc.invalidateQueries({ queryKey: ["all-products"] }),
      ]);
      onClose();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Could not save the product. Try again.")),
  });

  function set<K extends keyof ProductFormState>(
    key: K,
    value: ProductFormState[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Reveal every field error at once rather than one per submit.
    setTouched({ name: true, rate: true, currency: true, gst: true });
    if (form.name.trim() === "") {
      return;
    }
    const minor = parseMinorFromMajor(form.unit_rate_major);
    if (minor == null) {
      return;
    }
    const bps = parseInt(form.gst_rate_bps, 10);
    const attributes: Record<string, string> = {};
    for (const { key, value } of form.attributes) {
      const k = key.trim();
      if (k !== "") attributes[k] = value;
    }
    mutation.mutate({
      name: form.name.trim(),
      description: form.description,
      category: form.category.trim(),
      business_type: form.business_type,
      hsn_sac: form.hsn_sac.trim(),
      unit: form.unit.trim(),
      unit_rate_minor: minor,
      currency: form.currency.trim().toUpperCase(),
      tax_scheme: form.tax_scheme,
      gst_rate_bps: Number.isNaN(bps) ? 0 : bps,
      attributes,
      active: form.active,
      // Honoured on create; ignored on update, where the move above does it.
      catalog_id: form.catalog_id,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={mutation.isPending}
      wide
      title={product ? "Edit product" : "New product"}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="product-form"
            disabled={mutation.isPending || hasFieldError}
          >
            {mutation.isPending
              ? "Saving…"
              : product
                ? "Save changes"
                : "Create product"}
          </button>
        </>
      }
    >
      <form id="product-form" onSubmit={handleSubmit} className="stack-16">
        <div className="field">
          <label className="label" htmlFor="p-name">
            Name
          </label>
          <input
            id="p-name"
            className="input"
            type="text"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            onBlur={() => markTouched("name")}
            aria-invalid={nameError ? true : undefined}
            autoFocus
          />
          {nameError && <div className="field-error">{nameError}</div>}
        </div>

        <div className="field">
          <label className="label" htmlFor="p-catalog">
            Catalog
          </label>
          <select
            id="p-catalog"
            className="input"
            value={form.catalog_id}
            onChange={(e) => set("catalog_id", e.target.value)}
          >
            <option value="">Ungrouped</option>
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Ungrouped is a normal place for a product to live — not a problem to
            fix.
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="p-desc">
            Description
          </label>
          <textarea
            id="p-desc"
            className="input"
            rows={2}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div className="row gap-12">
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-category">
              Category
            </label>
            <input
              id="p-category"
              className="input"
              type="text"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-btype">
              Business type
            </label>
            <select
              id="p-btype"
              className="input"
              value={form.business_type}
              onChange={(e) =>
                set("business_type", e.target.value as BusinessType)
              }
            >
              {BUSINESS_TYPES.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="row gap-12">
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-hsn">
              HSN / SAC
            </label>
            <input
              id="p-hsn"
              className="input"
              type="text"
              value={form.hsn_sac}
              onChange={(e) => set("hsn_sac", e.target.value)}
              placeholder="optional"
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-unit">
              Unit
            </label>
            <input
              id="p-unit"
              className="input"
              type="text"
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
              placeholder="e.g. each, kg, kWh"
            />
          </div>
        </div>
        <div className="row gap-12">
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-rate">
              Unit rate ({form.currency || "—"})
            </label>
            <input
              id="p-rate"
              className="input"
              type="text"
              inputMode="decimal"
              value={form.unit_rate_major}
              onChange={(e) => set("unit_rate_major", e.target.value)}
              placeholder="1499.00"
            />
            <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
              Tax-INCLUSIVE: what the customer pays. Stored as bigint minor
              units.
            </div>
          </div>
          <div className="field" style={{ width: 120 }}>
            <label className="label" htmlFor="p-ccy">
              Currency
            </label>
            <input
              id="p-ccy"
              className="input"
              type="text"
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
              placeholder="INR"
            />
          </div>
        </div>
        <div className="row gap-12">
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-scheme">
              Tax scheme
            </label>
            <select
              id="p-scheme"
              className="input"
              value={form.tax_scheme}
              onChange={(e) => set("tax_scheme", e.target.value as TaxScheme)}
            >
              {TAX_SCHEMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label className="label" htmlFor="p-bps">
              GST rate (bps)
            </label>
            <input
              id="p-bps"
              className="input"
              type="number"
              min={0}
              max={10000}
              step={1}
              value={form.gst_rate_bps}
              onChange={(e) => set("gst_rate_bps", e.target.value)}
              disabled={form.tax_scheme !== "gst"}
            />
            <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
              {form.tax_scheme === "gst"
                ? "1800 = 18%"
                : form.tax_scheme === "solar_70_30"
                  ? "Fixed by the 70/30 scheme — not used."
                  : "No tax — not used."}
            </div>
          </div>
        </div>

        <div className="field">
          <label className="label">Attributes</label>
          <div className="stack-6">
            {form.attributes.map((pair, idx) => (
              <div className="row gap-8" key={idx}>
                <input
                  className="input"
                  type="text"
                  placeholder="key"
                  value={pair.key}
                  onChange={(e) => {
                    const next = [...form.attributes];
                    next[idx] = { ...next[idx], key: e.target.value };
                    set("attributes", next);
                  }}
                />
                <input
                  className="input"
                  type="text"
                  placeholder="value"
                  value={pair.value}
                  onChange={(e) => {
                    const next = [...form.attributes];
                    next[idx] = { ...next[idx], value: e.target.value };
                    set("attributes", next);
                  }}
                />
                <button
                  className="icon-btn"
                  type="button"
                  aria-label="Remove attribute"
                  onClick={() =>
                    set(
                      "attributes",
                      form.attributes.filter((_, i) => i !== idx),
                    )
                  }
                >
                  <I.X size={12} />
                </button>
              </div>
            ))}
            <button
              className="btn ghost sm"
              type="button"
              onClick={() =>
                set("attributes", [...form.attributes, { key: "", value: "" }])
              }
            >
              <I.Plus size={11} /> Add attribute
            </button>
          </div>
        </div>

        <label
          className="row gap-8"
          htmlFor="p-active"
          style={{ cursor: "pointer", alignItems: "center" }}
        >
          <input
            id="p-active"
            type="checkbox"
            checked={form.active}
            onChange={(e) => set("active", e.target.checked)}
          />
          <span style={{ fontSize: 13 }}>
            {form.active
              ? "Active — sellable"
              : "Inactive — hidden from checkout"}
          </span>
        </label>

        {error && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </form>
    </Modal>
  );
}
